import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import SiteAccessGate from './components/SiteAccessGate'

const AppLayout = lazy(() => import('./layouts/AppLayout'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const SetupPasswordPage = lazy(() => import('./pages/SetupPasswordPage'))
const MySubmissionDetailPage = lazy(() => import('./pages/MySubmissionDetailPage'))
const FormSettingsPage = lazy(() => import('./pages/forms/FormSettingsPage'))
const FormSubmissionsDataPanelPage = lazy(() => import('./pages/FormSubmissionsDataPanelPage'))
const FormSubmitPage = lazy(() => import('./pages/FormSubmitPage'))
const FormSubmissionsPage = lazy(() => import('./pages/FormSubmissionsPage'))
const ApprovalReviewPage = lazy(() => import('./pages/ApprovalReviewPage'))
const SubmissionProgressPage = lazy(() => import('./pages/SubmissionProgressPage'))
const FinalPdfLookupPage = lazy(() => import('./pages/FinalPdfLookupPage'))

function RouteLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  )
}

function AuthGuard({ children, require: mode }: Readonly<{ children: React.ReactNode; require: 'auth' | 'guest' }>) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (mode === 'auth') return user ? <>{children}</> : <Navigate to="/login" replace />
  return user ? <Navigate to="/app" replace /> : <>{children}</>
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/:slug/preview" element={<FormSubmitPage mode="preview" />} />
        <Route path="/approval/:submissionId" element={<ApprovalReviewPage />} />
        <Route path="/final-pdf" element={<FinalPdfLookupPage />} />
        <Route path="/submission-progress/:submissionId" element={<SubmissionProgressPage />} />
        <Route path="/:slug" element={<FormSubmitPage />} />
        <Route
          path="/setup-password"
          element={
            <AuthGuard require="guest">
              <SetupPasswordPage />
            </AuthGuard>
          }
        />
        <Route
          path="/login"
          element={
            <AuthGuard require="guest">
              <LoginPage />
            </AuthGuard>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AuthGuard require="guest">
              <ForgotPasswordPage />
            </AuthGuard>
          }
        />
        <Route
          path="/app"
          element={
            <AuthGuard require="auth">
              <AppLayout />
            </AuthGuard>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="my-submissions/:submissionId" element={<MySubmissionDetailPage />} />
          <Route path="forms/:slug/settings" element={<FormSettingsPage />} />
          <Route path="forms/:slug/submissions" element={<FormSubmissionsDataPanelPage />} />
          <Route path="forms/:slug/submissions/:submissionId" element={<FormSubmissionsPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <SiteAccessGate>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </SiteAccessGate>
  )
}
